// src/api.ts
// Shared fetch helpers for /api/covers/* and /api/cards/* clients.

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function handle<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;
  const data: Record<string, unknown> = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError(res.status, typeof data.error === 'string' ? data.error : `Request failed (${res.status})`);
  }
  return data as T;
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });
  return handle<T>(res);
}

/** Like apiFetch, but for multipart/form-data bodies — no Content-Type header, so the browser sets its own boundary. */
export async function apiFetchForm<T>(path: string, formData: FormData, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    method: 'POST',
    body: formData,
    ...init,
  });
  return handle<T>(res);
}

/** Like apiFetch, but for a binary (image) response — returns the Blob itself rather than parsing JSON. */
export async function apiFetchBlob(path: string, init?: RequestInit): Promise<Blob> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });
  if (!res.ok) {
    const data: Record<string, unknown> = await res.json().catch(() => ({}));
    throw new ApiError(res.status, typeof data.error === 'string' ? data.error : `Request failed (${res.status})`);
  }
  return res.blob();
}
